import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import { FileDownload, Refresh } from '@mui/icons-material';
import { DatePreset } from '../../utils/datePresets';

interface SupplierStatsFiltersBarProps {
  presets: DatePreset[];
  preset: string;
  startDate: Date;
  endDate: Date;
  onPresetChange: (label: string) => void;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onRefresh: () => void;
  onExport?: () => void;
  exporting?: boolean;
  exportDisabled?: boolean;
  children?: React.ReactNode;
}

const SupplierStatsFiltersBar: React.FC<SupplierStatsFiltersBarProps> = ({
  presets,
  preset,
  startDate,
  endDate,
  onPresetChange,
  onStartDateChange,
  onEndDateChange,
  onRefresh,
  onExport,
  exporting = false,
  exportDisabled = false,
  children,
}) => {
  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'center',
        '& .MuiTextField-root': { maxWidth: '100%' },
        '& .react-datepicker-wrapper': { maxWidth: '100%' },
      }}
    >
      <TextField
        select
        size="small"
        label="Period"
        value={preset}
        onChange={(e) => onPresetChange(e.target.value)}
        sx={{ minWidth: 200 }}
      >
        {presets.map((p) => (
          <MenuItem key={p.label} value={p.label}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>
      <DatePicker
        selected={startDate}
        onChange={(date) => {
          if (date) onStartDateChange(date);
        }}
        selectsStart
        startDate={startDate}
        endDate={endDate}
        dateFormat="yyyy-MM-dd"
        customInput={<TextField size="small" label="Från datum" />}
      />
      <DatePicker
        selected={endDate}
        onChange={(date) => {
          if (date) onEndDateChange(date);
        }}
        selectsEnd
        startDate={startDate}
        endDate={endDate}
        minDate={startDate}
        dateFormat="yyyy-MM-dd"
        customInput={<TextField size="small" label="Till datum" />}
      />
      <Button
        variant="outlined"
        startIcon={<Refresh />}
        onClick={onRefresh}
        size="small"
      >
        Uppdatera
      </Button>
      {onExport && (
        <Button
          variant="contained"
          startIcon={
            exporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileDownload />
            )
          }
          onClick={onExport}
          disabled={exporting || exportDisabled}
          size="small"
        >
          Exportera Excel
        </Button>
      )}
      {React.Children.toArray(children).length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiFormControl-root': {
              minWidth: 150,
              flexGrow: { xs: 1, md: 0 },
            },
          }}
        >
          {children}
        </Box>
      )}
    </Paper>
  );
};

export default SupplierStatsFiltersBar;
